import React, { createContext, useContext } from 'react';
import { useLocalStorage } from './useLocalStorage';

type ShowChart = {
  rate: boolean;
  histogram: boolean;
  interval: boolean;
  annotation: boolean;
  tachycardia: boolean;
  healthIndication: boolean;
};

type VisiblePeaks = {
  P: boolean;
  Q: boolean;
  R: boolean;
  S: boolean;
  T: boolean;
  E: boolean;
};

type VisibleAnnotations = {
  ML: boolean;
};

type VisibleIntervals = {
  PQ: boolean;
  QRS: boolean;
  QT: boolean;
  RR: boolean;
  U: boolean;
  /**
   * Automatic noise — the stretches the analysis cut out before R-peak
   * detection (project_management#552), on by default. A new key on purpose:
   * the old `MLNoise` (#542, off by default) is persisted as false in many
   * browsers and would otherwise hide the bands.
   */
  AutoNoise: boolean;
  AFib: boolean;
  PE: boolean;
  /** Sequence apnea-screening minutes (#555) — on by default; only present where the report shows apnea */
  Apnea: boolean;
};

export const CHART_HEIGHT_DEFAULT = 200;
export const CHART_HEIGHT_MIN = 100;
export const CHART_HEIGHT_MAX = 500;
export const CHART_HEIGHT_STEP = 25;

type ChartSettingsContext = {
  xAxisTime: boolean;
  scrollSpeed: number;
  allowCreatePointComment: boolean;
  jumpToML: boolean;
  showCharts: ShowChart;
  visiblePeaks: VisiblePeaks;
  visibleAnnotations: VisibleAnnotations;
  visibleIntervals: VisibleIntervals;
  chartHeight: number;
  setXAxisTime: React.Dispatch<React.SetStateAction<boolean>>;
  setScrollSpeed: React.Dispatch<React.SetStateAction<number>>;
  setAllowCreatePointComment: React.Dispatch<React.SetStateAction<boolean>>;
  setJumpToML: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCharts: React.Dispatch<React.SetStateAction<ShowChart>>;
  setVisiblePeaks: React.Dispatch<React.SetStateAction<VisiblePeaks>>;
  setVisibleAnnotations: React.Dispatch<React.SetStateAction<VisibleAnnotations>>;
  setVisibleIntervals: React.Dispatch<React.SetStateAction<VisibleIntervals>>;
  setChartHeight: React.Dispatch<React.SetStateAction<number>>;
  resetChartHeight: () => void;
};

const initialState = {
  xAxisTime: false,
  scrollSpeed: 1,
  allowCreatePointComment: false,
  jumpToML: false,
  showCharts: {
    rate: false,
    histogram: false,
    interval: false,
    annotation: false,
    tachycardia: false,
    healthIndication: false,
  },
  visiblePeaks: {
    P: false,
    Q: false,
    R: true,
    S: false,
    T: false,
    E: false,
  },
  visibleAnnotations: {
    // Automatic Rpeak annotations
    ML: false,
  },
  visibleIntervals: {
    PQ: false,
    QRS: false,
    QT: false,
    RR: true,
    U: true,
    AutoNoise: true,
    AFib: false,
    PE: false,
    Apnea: true,
  },
};

function useProvideSettings(): ChartSettingsContext {
  const [xAxisTime, setXAxisTime] = useLocalStorage<boolean>(
    'chart-settings/xAxis',
    initialState.xAxisTime,
  );
  const [scrollSpeed, setScrollSpeed] = useLocalStorage<number>(
    'chart-settings/scrollSpeed',
    initialState.scrollSpeed,
  );
  const [allowCreatePointComment, setAllowCreatePointComment] = useLocalStorage<boolean>(
    'chart-settings/pointComment',
    initialState.allowCreatePointComment,
  );
  const [jumpToML, setJumpToML] = useLocalStorage<boolean>(
    'chart-settings/jumpToML',
    initialState.jumpToML,
  );

  const [showCharts, setShowCharts] = useLocalStorage<ShowChart>(
    'chart-settings/showCharts',
    initialState.showCharts,
  );
  const [visiblePeaks, setVisiblePeaks] = useLocalStorage<VisiblePeaks>(
    'chart-settings/visiblePeaks',
    initialState.visiblePeaks,
  );
  const [visibleAnnotations, setVisibleAnnotations] = useLocalStorage<VisibleAnnotations>(
    'chart-settings/visibleAnnotations',
    initialState.visibleAnnotations,
  );
  const [storedVisibleIntervals, setVisibleIntervals] = useLocalStorage<VisibleIntervals>(
    'chart-settings/visibleIntervals',
    initialState.visibleIntervals,
  );
  // Objects persisted before a key existed (e.g. AutoNoise, #552) lack it;
  // fill from defaults so switches stay controlled and new keys get their default.
  const visibleIntervals: VisibleIntervals = { ...initialState.visibleIntervals, ...storedVisibleIntervals };
  const [chartHeight, setChartHeight] = useLocalStorage<number>(
    'chart-settings/chartHeight',
    CHART_HEIGHT_DEFAULT,
  );

  const resetChartHeight = () => {
    setChartHeight(CHART_HEIGHT_DEFAULT);
  };

  return {
    xAxisTime,
    scrollSpeed,
    allowCreatePointComment,
    jumpToML,
    showCharts,
    visiblePeaks,
    visibleAnnotations,
    visibleIntervals,
    chartHeight,
    setXAxisTime,
    setScrollSpeed,
    setAllowCreatePointComment,
    setJumpToML,
    setShowCharts,
    setVisiblePeaks,
    setVisibleAnnotations,
    setVisibleIntervals,
    setChartHeight,
    resetChartHeight,
  };
}

const initialContext = {
  ...initialState,
  chartHeight: CHART_HEIGHT_DEFAULT,
  setXAxisTime: () => {},
  setScrollSpeed: () => {},
  setAllowCreatePointComment: () => {},
  setJumpToML: () => {},
  setShowCharts: () => {},
  setVisiblePeaks: () => {},
  setVisibleAnnotations: () => {},
  setVisibleIntervals: () => {},
  setChartHeight: () => {},
  resetChartHeight: () => {},
};
const SettingsContext = createContext<ChartSettingsContext>(initialContext);

export function ProvideChartSettings({ children }) {
  const settings = useProvideSettings();

  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
}

export const useChartSettings = () => useContext(SettingsContext);
